// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The shell.
 *
 * One linear workflow — open a photograph, agree to the model, watch it move,
 * take the files — so the page is a handful of sections and exactly one of the
 * middle ones is visible at a time. No tabs, no settings panel. The controls
 * sit directly under the picture they change, because the argument of this tool
 * is that you should be adjusting them while looking at the result.
 */

import { term } from './glossary.ts';
import { ASPECTS, formatBytes } from './core/plan.ts';
import { PATHS } from './core/camera.ts';

export const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node as T;
};

const icon = (path: string, size = 18): string =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;

/* Lucide-shaped glyphs, drawn inline so no font or sprite is fetched. */
const ICON_MARK = '<path d="M3 17.5 9 11l4 4 3-3 5 5.5"/><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="8.5" r="1.4" fill="currentColor" stroke="none"/>';
const ICON_IMAGE = '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.8" cy="8.8" r="1.6"/><path d="m4 16 4.5-4.5 3.5 3.5 3-3L20 16"/>';
const ICON_SHIELD = '<path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6z"/><path d="m9 12 2 2 4-4"/>';
const ICON_DOWN = '<path d="M12 3v12"/><path d="m7 11 5 5 5-5"/><path d="M4 21h16"/>';
const ICON_ALERT = '<circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/>';

export function renderShell(): void {
  el('app').innerHTML = `
    <header class="site-header">
      <span class="brand">${icon(ICON_MARK, 22)} unflatten</span>
      <nav class="header-nav" aria-label="About this tool">
        <button class="nav-btn" id="nav-how" type="button">How it works</button>
        <button class="nav-btn" id="nav-privacy" type="button">Privacy</button>
        <button class="nav-btn" id="nav-about" type="button">About</button>
        <button class="nav-btn" id="log-toggle" type="button" aria-expanded="false">
          Log <span class="log-count" id="log-count">0</span>
        </button>
      </nav>
    </header>

    <main class="main-content">
      <section class="intro" id="intro">
        <h1>Give a flat photograph depth.</h1>
        <p class="lede">
          Open a picture — a scan of an old print, a phone snap, anything flat — and unflatten works out
          how far away everything in it is, then moves a camera through it. You leave with a clip that
          loops, a proper ${term('depthmap', 'depth map')}, and a lens blur you can put wherever you
          like. It all happens on this machine: the photograph is never uploaded, there is nothing to
          sign up for and nothing counts how many you make.
        </p>
        <button class="trust-badge" id="trust-badge" type="button">
          ${icon(ICON_SHIELD, 16)} Runs entirely in your browser. Your photo never leaves this tab — read how
        </button>
      </section>

      <section id="stage-drop">
        <div class="dropzone" id="dropzone" role="button" tabindex="0"
             aria-label="Choose a photograph, or drop one here">
          ${icon(ICON_IMAGE, 34)}
          <p class="dz-title">Drop a photograph here</p>
          <p class="dz-hint">or tap to choose one · JPEG, PNG, WebP, AVIF · you can also paste with ⌘V</p>
          <p class="dz-privacy">It is read from your disk and stays in this tab. It is not uploaded.</p>
          <div class="samples">
            <span>Never used it before?</span>
            <button class="sample-pill" id="sample-portrait" type="button">
              Try a portrait <span class="pill-tag">hard edge</span>
            </button>
            <button class="sample-pill" id="sample-landscape" type="button">
              Try a landscape <span class="pill-tag">soft depth</span>
            </button>
          </div>
        </div>
        <p class="scope-note">
          unflatten builds a believable scene from one picture — it does not measure it, and where the
          foreground pulls away from the background it has to
          ${term('disocclusion', 'invent the pixels behind it')}.
          <button class="linklike" id="limits-link" type="button">What it can and cannot do</button>
        </p>
      </section>

      <section id="stage-consent" hidden></section>

      <section id="stage-progress" hidden>
        <div class="progress-panel">
          <div class="progress-row">
            <span id="progress-phase">Starting…</span>
            <span id="progress-count"></span>
          </div>
          <div class="progress-track"><div class="progress-fill" id="progress-fill"></div></div>
          <div class="progress-row progress-sub">
            <span id="progress-rate"></span>
            <span id="progress-note"></span>
          </div>
          <div class="action-row" style="margin-top:0.75rem">
            <button class="btn btn-sm" id="progress-cancel" type="button">Stop</button>
          </div>
        </div>
      </section>

      <section id="stage-studio" hidden>
        <div class="plate" id="plate">
          <canvas id="preview" aria-label="A live preview of the moving photograph"></canvas>
          <span class="plate-note" id="plate-note" hidden>Red pixels were invented</span>
        </div>

        <div class="controls">
          <div class="control-row">
            <label id="label-path">Movement</label>
            <div class="seg" id="seg-path" role="group" aria-labelledby="label-path"></div>
          </div>

          <div class="control-row">
            <label id="label-aspect">Shape</label>
            <div class="seg" id="seg-aspect" role="group" aria-labelledby="label-aspect"></div>
          </div>

          <div class="control-row">
            <label for="range-strength">Depth of travel</label>
            <input type="range" id="range-strength" min="10" max="150" step="5" value="100" />
            <span class="value" id="value-strength">100%</span>
          </div>

          <div class="control-row">
            <label for="range-focus">${term('focus', 'Focal plane')}</label>
            <input type="range" id="range-focus" min="0" max="100" step="1" value="50" />
            <span class="value" id="value-focus">mid</span>
          </div>

          <div class="control-row">
            <label for="range-seconds">Length</label>
            <input type="range" id="range-seconds" min="2" max="8" step="1" value="3" />
            <span class="value" id="value-seconds">3 s</span>
          </div>

          <p class="cap-note" id="cap-note"></p>

          <div class="control-row">
            <label class="toggle">
              <input type="checkbox" id="toggle-holes" />
              ${term('holemap', 'Show me the invented pixels')}
            </label>
          </div>

          <div class="action-row">
            <button class="btn btn-sm" id="play-toggle" type="button">Pause</button>
          </div>

          <div class="action-row">
            <button class="btn btn-primary" id="make-clip" type="button">${icon(ICON_DOWN, 16)} Make the clip</button>
            <button class="btn" id="make-depth" type="button">Save the depth map</button>
            <button class="btn" id="make-bokeh" type="button">Save the lens blur</button>
            <button class="btn" id="make-anaglyph" type="button">Save the 3D still</button>
            <button class="btn" id="pick-other" type="button">Another photograph</button>
          </div>
        </div>

        <div class="artefacts" id="artefacts" hidden></div>
      </section>

      <section id="error-panel" class="error-panel" hidden></section>
    </main>

    <footer class="site-footer">
      <div class="footer-inner">
        Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      </div>
    </footer>

    <input type="file" id="file-input" accept="image/*" hidden />
  `;

  const paths = el('seg-path');
  paths.innerHTML = PATHS.map(
    (p, i) =>
      `<button type="button" data-path="${p.id}" aria-pressed="${i === 0}" title="${escapeHtml(p.hint)}">${p.label}</button>`,
  ).join('');

  const aspects = el('seg-aspect');
  aspects.innerHTML = ASPECTS.map(
    (a, i) =>
      `<button type="button" data-aspect="${a.id}" aria-pressed="${i === 0}" title="${escapeHtml(a.hint)}">${a.label}</button>`,
  ).join('');
}

/**
 * The consent panel.
 *
 * The download is tens of megabytes and it is the first thing that would
 * surprise somebody, so it is named in full — the tier, the exact size, the
 * host — and it never starts on its own.
 */
export function renderConsent(
  facts: { name: string; width: number; height: number; bytes: number },
  tier: { label: string; bytes: number; runtimeBytes: number; slow: boolean },
): string {
  const total = Math.round((tier.bytes + tier.runtimeBytes) / 1e6);
  return `
    <div class="panel">
      <div class="panel-head">
        <h2 class="panel-title">${escapeHtml(facts.name)}</h2>
        <span class="panel-meta">${facts.width}×${facts.height} · ${formatBytes(facts.bytes)}</span>
      </div>
      <div class="panel-body">
        <p class="plan-line">
          To work out the depth, unflatten needs a model it does not ship with. It is about
          <strong>${total} MB</strong>, downloaded once from Hugging Face and then kept by your browser
          — after that this tool works with the network off.
        </p>
        <p class="plan-line">
          It will run on <strong>${tier.label}</strong>.
          ${
            tier.slow
              ? `This browser has no ${term('webgpu', 'WebGPU')}, so the model runs on a single processor
                 core and one photograph takes <strong>several seconds</strong> rather than a fraction of
                 one. It still works; it is just slower.`
              : `That takes well under a second for a photograph of ordinary size.`
          }
        </p>
        <p class="fine">
          Your photograph is not part of this. The download happens before it is looked at, and nothing
          is ever sent back.
        </p>
        <div class="action-row">
          <button class="btn btn-primary" id="consent-go" type="button">Get the model (${total} MB) and continue</button>
          <button class="btn" id="consent-cancel" type="button">Not now</button>
        </div>
      </div>
    </div>
  `;
}

export function setProgress(phase: string, done: number, total: number, rate = '', note = ''): void {
  el('progress-phase').textContent = phase;
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  // `total === 0` means a setup step with nothing to count. The bar is left
  // where it was rather than snapped back to zero, so a sequence of named
  // stages reads as movement instead of as three restarts.
  if (total > 0) {
    el<HTMLElement>('progress-fill').style.width = `${pct.toFixed(1)}%`;
    el('progress-count').textContent = `${Math.round(pct)}%`;
  }
  el('progress-rate').textContent = rate;
  el('progress-note').textContent = note;
}

export function showError(message: string, retry?: () => void): void {
  const panel = el('error-panel');
  panel.innerHTML = `
    <p class="error-title">${icon(ICON_ALERT, 18)} That did not work</p>
    <p class="error-detail">${escapeHtml(message)}</p>
  `;
  if (retry) {
    const row = document.createElement('div');
    row.className = 'action-row';
    const button = document.createElement('button');
    button.className = 'btn btn-primary';
    button.type = 'button';
    button.textContent = 'Try again';
    button.addEventListener('click', () => {
      panel.hidden = true;
      retry();
    });
    row.append(button);
    panel.append(row);
  }
  panel.hidden = false;
}

export function clearError(): void {
  el('error-panel').hidden = true;
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

export { icon, ICON_DOWN };
